/**
 * The single styling home for admin form-card chrome (the tabbed header on
 * multi-tab forms, the single-section header, and the padded content region).
 *
 * Every admin form-card part imports these constants, so a change to the teal
 * heading colour / tab sizing / content padding lands in ONE file rather than
 * being scavenger-hunted across `ExceptionTypeForm`, `FranchisePartnerForm`,
 * `UserForm`, `AdminFormSectionHeader`, etc. No JSX, no logic - just `sx`.
 */
import type { SxProps, Theme } from '@mui/material'
import { poolwerxColors } from '../../../theme/colors'

/** Teal-accented tab styling shared by the tabbed header and section header. */
export const formHeaderTabsSx: SxProps<Theme> = {
  '& .MuiTab-root': {
    minHeight: 64,
    textTransform: 'none',
    fontSize: '0.95rem',
  },
  '& .Mui-selected': { color: poolwerxColors.teal },
  '& .MuiTabs-indicator': { backgroundColor: poolwerxColors.teal },
}

/** Wrapper around the tab row - the grey hairline separating header from fields. */
export const formHeaderBoxSx: SxProps<Theme> = {
  borderBottom: 1,
  borderColor: 'divider',
}

/**
 * The shared "admin form field surface" rules: outlined inputs paint white
 * (`background.paper`) rather than letting the page/panel fill show through,
 * and helper sub-text sits flush under the field (MUI's default 14px left
 * indent reads as a stray tab). Spread into BOTH the page card content (below)
 * AND a form dialog's body (pass as `BaseDialog`'s `contentSx`) so inputs look
 * identical whether the form lives on a page card or inside a modal.
 */
export const formFieldSurfaceSx = {
  '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
  '& .MuiFormHelperText-root': { color: 'rgba(0, 0, 0, 0.72)', marginLeft: 0, marginRight: 0 },
}

/** Padded content region rendered below the header, inside the card. */
export const formCardContentSx: SxProps<Theme> = {
  p: 3,
  ...formFieldSurfaceSx,
}
